import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Menu, X, BookOpen, Globe, User, LogOut, MessageCircle } from "lucide-react";
import { OPEN_ASSISTANT_EVENT } from "@/components/ChatWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { lang, t, toggle } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const items = [
    { to: "/books", label: t("nav_books"), testid: "nav-books" },
    { to: "/authors", label: t("nav_authors"), testid: "nav-authors" },
    { to: "/publishers", label: t("nav_publishers"), testid: "nav-publishers" },
    { to: "/discussions", label: t("nav_discussions"), testid: "nav-discussions" },
  ];

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-background/90 backdrop-blur border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 gap-4">
          <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="brand-logo" aria-label={t("site_name")}>
            <BookOpen size={20} className="text-primary shrink-0" aria-hidden="true" />
            <span className="font-display text-[1.15rem] sm:text-xl leading-none text-primary truncate">हिन्दी पुस्तकें और प्रकाशक</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 ml-4">
            {items.map(i => (
              <Link
                key={i.to} to={i.to} data-testid={i.testid}
                aria-current={location.pathname.startsWith(i.to) ? "page" : undefined}
                className={`px-3 py-2 text-[0.98rem] rounded-sm transition-colors hover:text-primary
                  ${location.pathname.startsWith(i.to) ? "text-primary" : "text-foreground/80"}`}
              >{i.label}</Link>
            ))}
          </nav>

          <form onSubmit={onSearch} role="search" className="flex-1 max-w-sm ml-auto hidden lg:block">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input
                value={q} onChange={e => setQ(e.target.value)}
                placeholder={t("search_placeholder")}
                aria-label={t("search_placeholder")}
                className="pl-9 bg-card border-border focus-visible:bg-background"
                data-testid="global-search-input"
              />
            </div>
          </form>

          <Button variant="ghost" size="icon" className="xl:hidden shrink-0 ml-auto lg:ml-0" data-testid="assistant-btn"
            aria-label={lang === "hi" ? "सहायक से पूछें" : "Ask the assistant"}
            onClick={() => window.dispatchEvent(new Event(OPEN_ASSISTANT_EVENT))}>
            <MessageCircle size={18} aria-hidden="true" />
          </Button>
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="lang-toggle" aria-label={lang === "hi" ? "Switch to English" : "हिन्दी में देखें"} className="hidden lg:inline-flex shrink-0 w-auto px-2">
            <Globe size={16} />
            <span className="ml-1 text-xs">{lang.toUpperCase()}</span>
          </Button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" aria-label={user.name || t("nav_profile")} className="w-9 h-9 shrink-0 rounded-full overflow-hidden ring-1 ring-border hover:ring-primary transition">
                  {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-secondary flex items-center justify-center text-sm">{user.name?.[0] || "U"}</div>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile"><User size={14} className="mr-2" />{t("nav_profile")}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout"><LogOut size={14} className="mr-2" />{t("nav_logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button onClick={() => nav("/login")} size="sm" data-testid="btn-login" className="hidden lg:inline-flex">
              {t("nav_login")}
            </Button>
          )}

          <Button variant="ghost" size="icon" className="lg:hidden shrink-0" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn" aria-label="Menu" aria-expanded={mobileOpen}>
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
        </div>

        {mobileOpen && (
          <div className="lg:hidden pb-4 space-y-2">
            <form onSubmit={onSearch}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder={t("search_placeholder")} aria-label={t("search_placeholder")} className="pl-9" data-testid="mobile-search-input" />
              </div>
            </form>
            {items.map(i => (
              <Link key={i.to} to={i.to} className="block px-3 py-3 hover:bg-secondary/60 rounded-sm" data-testid={`mobile-${i.testid}`}>{i.label}</Link>
            ))}
            <button type="button" onClick={toggle} className="flex w-full items-center gap-2 px-3 py-3 hover:bg-secondary/60 rounded-sm" data-testid="mobile-lang-toggle">
              <Globe size={16} aria-hidden="true" />{lang === "hi" ? "English" : "हिन्दी"}
            </button>
            {!user && <Button onClick={() => nav("/login")} size="sm" className="w-full" data-testid="mobile-btn-login">{t("nav_login")}</Button>}
          </div>
        )}
      </div>
    </header>
  );
};
